// This file define the custom validator that is used on the client side to evaluate Pattern Match text validation rules
// The logic in here should produce identical results to the logic in PatternMatchValidator.cs

import * as validationHelpers from "./textValidationRuleValidationHelpers";

export function validate(value, ruleSchema) {
    if (value === null || value === "") {
        return true;
    }

    for (const pattern of ruleSchema.allowedPatterns) {
        if (pattern === "") {
            return pattern === value;
        }

        if (inputMatchesPattern(value, pattern)) {
            return true;
        }
    }

    return false;
}

const ESCAPE_SEQUENCE_TYPE = {
    hash: "\\#",
    uppercaseA: "\\A",
    lowercaseA: "\\a",
    uppercaseX: "\\X",
    asterisk: "\\*",
    backslash: "\\\\"
};

const PATTERN_CHARACTER_TYPE = {
    hash: "#",
    uppercaseA: "A",
    lowercaseA: "a",
    uppercaseX: "X",
    asterisk: "*",
    backslash: "\\"
};

const ESCAPE_SEQUENCE_TO_PATTERN_CHAR_MAP = getEscapeSequenceToPatternMap();
const DEFINED_PATTERN_CHARACTERS = new Set(Object.values(PATTERN_CHARACTER_TYPE));

function getEscapeSequenceToPatternMap() {
    const escapeSequenceToPatternMap = {};

    for (const k of Object.keys(ESCAPE_SEQUENCE_TYPE)) {
        escapeSequenceToPatternMap[ESCAPE_SEQUENCE_TYPE[k]] = PATTERN_CHARACTER_TYPE[k];
    }
    return escapeSequenceToPatternMap;
}

function inputMatchesPattern(input, pattern) {
    let patternIndex = 0;

    for (const c of input) {
        if (patternIndex >= pattern.length) {
            return false;
        }

        if (patternIsAtEscapeSequence(pattern, patternIndex)) {
            if (!validateEscapeSequence(c, pattern, patternIndex)) {
                return false;
            }

            // need to skip ahead one index since the escape sequence is 2 characters that map to 1 input character
            patternIndex++;
        } else if (patternIsAtDefinedCharacter(pattern, patternIndex)) {
            if (!inputCharMatchesPatternChar(c, pattern[patternIndex])) {
                return false;
            }
        } else if (c !== pattern[patternIndex]) {
            return false;
        }

        patternIndex++;
    }

    return patternIndex === pattern.length;
}

function patternIsAtDefinedCharacter(pattern, patternIndex) {
    return DEFINED_PATTERN_CHARACTERS.has(pattern[patternIndex]);
}

//if pattern[i] is a backslash, it can't be the last character (unless it is the second of 2 backslashes)
//if pattern[i] is a backslash, the only valid characters that could follow are pattern characters that have escape sequences
//a pattern having a single backslash at the end is like having a dangling, invalid escape sequence.
function patternIsAtEscapeSequence(pattern, patternIndex) {
    return pattern[patternIndex] === PATTERN_CHARACTER_TYPE.backslash;
}

function validateEscapeSequence(inputChar, pattern, patternIndex) {
    if (inputChar.length !== 1) {
        return false;
    }

    if (patternIndex >= pattern.length - 1) {
        return false;
    }

    const patternChunk = pattern.substring(patternIndex, patternIndex + 2);

    if (ESCAPE_SEQUENCE_TO_PATTERN_CHAR_MAP.hasOwnProperty(patternChunk)) {
        return inputChar === ESCAPE_SEQUENCE_TO_PATTERN_CHAR_MAP[patternChunk];
    } else {
        return false;
    }
}

function inputCharMatchesPatternChar(inputChar, patternChar) {
    switch (patternChar) {
        case PATTERN_CHARACTER_TYPE.hash:
            return validationHelpers.isNumericDigit(inputChar);
        case PATTERN_CHARACTER_TYPE.uppercaseA:
            return validationHelpers.isUppercaseLetter(inputChar);
        case PATTERN_CHARACTER_TYPE.lowercaseA:
            return validationHelpers.isLowercaseLetter(inputChar);
        case PATTERN_CHARACTER_TYPE.uppercaseX:
            return validationHelpers.isLetterAnyCase(inputChar);
        case PATTERN_CHARACTER_TYPE.asterisk:
            return validationHelpers.isLetterOrNumericDigit(inputChar);
        default:
            return false;
    }
}
